import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';
import moment from 'moment';

import Layout from '../../shared/components/Layout/Layout';
import WorkoutsWrapper from './components/presentational/WorkoutsWrapper';
import WorkoutCarouselContainer from './components/presentational/WorkoutCarouselContainer';
import MediaModalContainer from './components/presentational/MediaModalContainer';

import OptOutModalContainer from './components/presentational/OptOutModalContainer';
import RestTimerModalContainer from './components/presentational/RestTimerModalContainer';
import RecordImageModalContainer from './components/presentational/RecordWorkoutsImageModalContainer';
import RecordVideoModalContainer from './components/presentational/RecordWorkoutsVideoModalContainer';
import WorkoutHistoryModalContainer from './components/presentational/WorkoutHistoryModalContainer';
import QuickJournalModalContainer from './components/presentational/QuickJournalModalContainer';
import DatePickerOverlayContainer from './components/presentational/DatePickerOverlayContainer';

import {
  getAthletes,
  setSharedAthlete,
  setActiveWorkoutDate,
  wipeOutNonDate,
  setIsOrganizationLockedModalShowing,
  fetchInitialWorkoutProgramWorkouts,
  fetchWorkoutPrograms,
  setIsDayCarousel,
} from './ducks/workoutsActions';
import { fetchBodyMapData } from '../recovery-radar-tracker/ducks/recoveryRadarTrackerActions';
import JournalEntryModalContainer from './components/presentational/JournalEntryModalContainer';
import ExertionScoreModalContainer from './components/presentational/ExertionScoreModalContainer';
import PrintingOptionsModalContainer from './components/presentational/PrintingOptionsModalContainer';
import AdditionalInfoModalContainer from './components/presentational/AdditionalInfoModalContainer';
import ModalOrganizationLocked from '../../shared/components/ModalOrganizationLocked';

import getAllUrlParams from '../../shared/utils/getAllUrlParams';
import TagSelectionModalContainer from './components/presentational/TagSelectionModalContainer';
import MultiQuestionModalContainer from './components/presentational/MultiQuestionModalContainer';
import ProgramListBoxModalContainer from './components/presentational/ProgramListBoxModalContainer';
import ResetProgramModalContainer from './components/presentational/ResetProgramModalContainer';
import CompleteWorkoutModalContainer from './components/presentational/CompleteWorkoutModalContainer';
import SkipWorkoutModalContainer from './components/presentational/SkipWorkoutModalContainer';
import CompleteProgramModalContainer from './components/presentational/CompleteProgramModalContainer';
import ProgramInformationModalContainer from './components/presentational/ProgramInformationModalContainer';
import RecoveryRadarTracker from '../recovery-radar-tracker/RecoveryRadarTracker';
import BottomSheetContainer from './components/presentational/BottomSheetContainer';

const PageWrapper = styled('div')`
  display: block;
  flex-direction: row;
  height: ${(props) => (props.hideNav ? '100%' : 'calc(100% - 49px)')};
  margin-top: ${(props) => (props.hideNav ? '0' : '49px')};
  @media only screen and (max-width : 768px) {
    height: calc(100%);
  }
  background: #F5F5F5;
  justify-content: center;
  overflow: scroll;
`;

const MarginComponent = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Workouts = () => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const sharedAthlete = useSelector((state) => state.workouts.data.sharedAthlete);
  const userId = currentUser.admin ? sharedAthlete?.id : currentUser.id;
  const activeWorkoutDate = useSelector((state) => state.workouts.data.activeWorkoutDate);
  const isOrganizationLockedModalShowing = useSelector(
    (state) => state.workouts.ui.isOrganizationLockedModalShowing,
  );
  const workoutPrograms = useSelector((state) => state.workouts.data.workoutPrograms);
  const athletes = useSelector((state) => state.workouts.data.athletes);

  const dispatch = useDispatch();
  const lastLoadedProgramRef = useRef('');
  const pendingProgramFetchRef = useRef('');
  const hasWipedFromCurrentUserRef = useRef(false);

  const urlParams = getAllUrlParams(window.location.href, false);
  const hideNav = urlParams.hideNav === 'true';
  const getParams = () => getAllUrlParams(window.location.href, false);

  // Program URL helpers when coming from Navigator //
  const hasProgramRouteParams = (params) => (
    !!(params.programId && params.dayNum !== undefined)
  );

  const isValidProgramParams = (params) => (
    params.programId
    && params.dayNum !== undefined
    && userId
    && currentUser.accountCode
  );

  const resolveProgramId = (params) => {
    const dayBasedProgramId = params.programId;
    if (!params.instanceId) return dayBasedProgramId;
    const match = workoutPrograms.find(
      (p) => p.dayBasedInstanceId === Number(params.instanceId),
    );

    return match ? match.dayBasedProgramId : dayBasedProgramId;
  };

  const hasInstanceProgramMatch = (params) => (
    !!workoutPrograms.find((p) => p.dayBasedInstanceId === Number(params.instanceId))
  );

  const getProgramName = (dayBasedProgramId) => {
    const programData = workoutPrograms.find(
      (p) => p.dayBasedProgramId === Number(dayBasedProgramId),
    );

    return programData?.programName || 'Program';
  };

  // Clear program params to avoid stale data when switching athletes/performing other actions
  const clearProgramParamsFromUrl = () => {
    const params = getParams();
    if (!hasProgramRouteParams(params)) return;

    const cleanedParams = { ...params };
    delete cleanedParams.programId;
    delete cleanedParams.instanceId;
    delete cleanedParams.dayNum;

    const nextSearch = new URLSearchParams(cleanedParams).toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    window.history.replaceState({}, '', nextUrl);
  };

  const closeOrganizationLockedModal = () => {
    batch(() => {
      dispatch(setIsOrganizationLockedModalShowing(false));
      if (currentUser.admin) {
        dispatch(setSharedAthlete({}));
      }
    });
  };

  useEffect(() => {
    // If the user is an admin, well fetch the list of athletes they are able to choose from here
    if (currentUser.admin) {
      dispatch(getAthletes(currentUser.accountCode));
      // Only wipe once as currentUser can update again mid-session
      if (!hasWipedFromCurrentUserRef.current) {
        const params = getParams();
        if (!hasProgramRouteParams(params)) {
          dispatch(wipeOutNonDate());
        }
        hasWipedFromCurrentUserRef.current = true;
      }
    } else {
      // Otherwise, we set selected athlete to the current user
      dispatch(setSharedAthlete(currentUser));
    }
  }, [currentUser]);

  useEffect(() => {
    const params = getAllUrlParams(window.location.href, false);
    if (params.date) {
      dispatch(setActiveWorkoutDate(params.date));
    }
  }, []);

  // If admin arrives with athleteId in URL then set full sharedAthlete so new tab renders correctly
  useEffect(() => {
    const params = getAllUrlParams(window.location.href, false);
    const athleteId = params.athleteId ? Number(params.athleteId) : null;
    if (!athleteId || !currentUser?.admin) return;

    if (athletes?.length) {
      const match = athletes.find((a) => Number(a.id) === athleteId);
      if (match) {
        dispatch(setSharedAthlete(match));
        return;
      }
    }

    dispatch(getAthletes(currentUser.accountCode));
  }, [currentUser?.admin, currentUser?.accountCode, athletes]);

  useEffect(() => {
    if (currentUser.admin) {
      const params = getParams();
      if (!hasProgramRouteParams(params)) {
        dispatch(wipeOutNonDate());
      }
    }
  }, [sharedAthlete]);

  // Load program workout from params when coming from athlete results in navigator
  useEffect(() => {
    const params = getParams();
    if (!isValidProgramParams(params)) {
      lastLoadedProgramRef.current = '';
      pendingProgramFetchRef.current = '';
      return;
    }
    const programLoadKey = `${params.programId}-${params.instanceId || 'none'}-${params.dayNum}-${userId}`;
    if (lastLoadedProgramRef.current === programLoadKey) return;

    dispatch(setIsDayCarousel(true));
    // Fetch program metadata if not already loaded
    if (params.instanceId && !hasInstanceProgramMatch(params)) {
      const fetchKey = `${programLoadKey}-${workoutPrograms?.length || 0}`;
      if (pendingProgramFetchRef.current !== fetchKey) {
        pendingProgramFetchRef.current = fetchKey;
        dispatch(fetchWorkoutPrograms(currentUser, userId));
      }
      return;
    }
    pendingProgramFetchRef.current = '';

    const dayBasedProgramId = resolveProgramId(params);
    const programName = getProgramName(dayBasedProgramId);
    const dayNum = Number(params.dayNum);

    dispatch(fetchInitialWorkoutProgramWorkouts(
      currentUser,
      { dayBasedProgramId: Number(dayBasedProgramId), programName },
      dayNum,
      userId,
      { dayNum, dayTitle: `Day ${dayNum + 1}`, weekTitle: `Week ${Math.floor(dayNum / 7) + 1}` },
    ));
    clearProgramParamsFromUrl();

    lastLoadedProgramRef.current = programLoadKey;
  }, [userId, currentUser?.accountCode, workoutPrograms]);

  // Fetch body map data if not already loaded
  useEffect(() => {
    const isFutureDate = moment(activeWorkoutDate).isAfter(moment(), 'day');

    if (Object.keys(sharedAthlete).length > 0 && activeWorkoutDate && !isFutureDate) {
      dispatch(fetchBodyMapData(
        currentUser,
        userId,
        activeWorkoutDate,
        false,
      ));
    }
  }, [sharedAthlete, activeWorkoutDate]);

  // this component is accompanied by a series of containers that handle the various modals
  return (
    <Layout
      fullWidth
    >
      <Helmet>
        <title>Workouts | TeamBuildr</title>
      </Helmet>
      <PrintingOptionsModalContainer />
      <OptOutModalContainer />
      <RestTimerModalContainer />
      <RecordImageModalContainer />
      <RecordVideoModalContainer />
      <WorkoutHistoryModalContainer />
      <JournalEntryModalContainer />
      <ExertionScoreModalContainer />
      <QuickJournalModalContainer />
      <DatePickerOverlayContainer />
      <AdditionalInfoModalContainer />
      <TagSelectionModalContainer />
      <MultiQuestionModalContainer />
      <ProgramListBoxModalContainer />
      <ResetProgramModalContainer />
      <CompleteWorkoutModalContainer />
      <SkipWorkoutModalContainer />
      <CompleteProgramModalContainer />
      <ProgramInformationModalContainer />
      <RecoveryRadarTracker modalContext='workoutEntry' />
      {isOrganizationLockedModalShowing && (
        <ModalOrganizationLocked
          isOpen={isOrganizationLockedModalShowing}
          onRequestClose={closeOrganizationLockedModal}
        />
      )}
      <PageWrapper hideNav={hideNav}>
        <WorkoutCarouselContainer />
        <MarginComponent>
          <WorkoutsWrapper sharedAthlete={sharedAthlete} />
        </MarginComponent>
      </PageWrapper>
      <BottomSheetContainer />
      <MediaModalContainer />
    </Layout>
  );
};

Workouts.propTypes = { };
export default Workouts;
